import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';

import { useLanguage } from '../../../hooks';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getObjects } from '../../../store/modules/listObject.slice';
import { getLocations } from '../../../store/modules/locations.slice';
import ItemComponent from './ItemComponent';
import ListComponent from './ListComponent';

const styles: Record<string, SxProps<Theme>> = {
  mainContainer: {
    flex: 0.7,
    display: 'flex',
    margin: '7px 0',
    maxHeight: '76vh',
  },
  card: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    padding: '0 10px',
  },
  title: {
    fontSize: '12px',
    color: '#757575',
    textAlign: 'center',
    margin: '10px 0 5px 0',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
  },
  body: {
    width: '100%',
    overflowY: 'auto',
    flex: 1,
  },
  loading: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowAble: {
    color: '#3E8FFA',
    fontSize: '14px',
    cursor: 'pointer',
    marginLeft: '10px',
  },
  arrowDisable: {
    color: '#e7eaec',
    fontSize: '14px',
    marginLeft: '10px',
  },
  page: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: '12px',
    color: '#757575',
    marginTop: '5px',
    alignItems: 'center',
  },
  container: {
    fontSize: '12px',
    display: 'flex',
    padding: '5px',
    maxWidth: '20vw',
  },
  tag: {
    marginRight: '5px',
    color: '#757575',
  },
  text: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};

const ListInformation = () => {
  const {
    list,
    objects,
    loading: objLoading,
    to: objTo,
    from: objFrom,
    page: objPage,
    pages: objPages,
    total: objTotal,
  } = useAppSelector((state) => state.listObjects);

  const {
    locations,
    loading: locLoading,
    to: locTo,
    from: locFrom,
    page: locPage,
    pages: locPages,
    total: locTotal,
  } = useAppSelector((state) => state.locations);

  const { showPanel, rightColumn } = useAppSelector((state) => state.listManager);
  const dispatch = useAppDispatch();
  const lang = useLanguage();

  return (
    <Fade in={!!rightColumn && !showPanel}>
      <Box sx={styles.mainContainer}>
        <Card sx={styles.card}>
          <Typography sx={styles.title}>{lang.listManager.dataListSource}</Typography>
          {objLoading || locLoading ? (
            <Box sx={styles.loading}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={styles.header}>
                {rightColumn === 'OBJECTS' && list && <ListComponent userList={list} />}
                {['LOCATIONS', 'ASSETS'].includes(rightColumn) && (
                  <Box sx={styles.container}>
                    <Typography sx={styles.tag}>{lang.listManager.name}</Typography>
                    <Typography sx={styles.text}>
                      {rightColumn === 'LOCATIONS'
                        ? lang.listManager.locations
                        : lang.listManager.assets}
                    </Typography>
                  </Box>
                )}
                <Divider
                  orientation={'horizontal'}
                  style={{ width: '100%' }}
                />
                {objects?.length > 0 || locations?.length > 0 ? (
                  <Typography sx={styles.title}>{lang.listManager.listItems}</Typography>
                ) : (
                  <Typography sx={styles.title}>{lang.listManager.emptyList}</Typography>
                )}
              </Box>
              <Box sx={styles.body}>
                {['OBJECTS', 'ASSETS'].includes(rightColumn) &&
                  objects?.map((item) => (
                    <ItemComponent
                      name={item.name}
                      key={item.code}
                    />
                  ))}
                {rightColumn === 'LOCATIONS' &&
                  locations?.map((item: { name: string; code: string }) => (
                    <ItemComponent
                      name={item.name}
                      key={item.code}
                    />
                  ))}
              </Box>
              {(rightColumn === 'OBJECTS' || rightColumn === 'ASSETS') && objects?.length > 0 && (
                <Box sx={styles.page}>
                  {objFrom} - {objTo} {lang.listManager.of} {objTotal}
                  <ArrowBackIosIcon
                    onClick={() => {
                      if (objPage !== 1)
                        dispatch(
                          getObjects({
                            metadataCode: rightColumn === 'OBJECTS' ? list?.code : 'assets',
                            page: objPage - 1,
                          }),
                        );
                    }}
                    sx={objPage !== 1 ? styles.arrowAble : styles.arrowDisable}
                  />
                  <ArrowForwardIosIcon
                    onClick={() => {
                      if (objPage !== objPages)
                        dispatch(
                          getObjects({
                            metadataCode: rightColumn === 'OBJECTS' ? list?.code : 'assets',
                            page: objPage + 1,
                          }),
                        );
                    }}
                    sx={objPage !== objPages ? styles.arrowAble : styles.arrowDisable}
                  />
                </Box>
              )}
              {rightColumn === 'LOCATIONS' && locations?.length > 0 && (
                <Box sx={styles.page}>
                  {locFrom} - {locTo} {lang.listManager.of} {locTotal}
                  <ArrowBackIosIcon
                    onClick={() => {
                      if (locPage !== 1)
                        dispatch(
                          getLocations({
                            page: locPage - 1,
                          }),
                        );
                    }}
                    sx={locPage !== 1 ? styles.arrowAble : styles.arrowDisable}
                  />
                  <ArrowForwardIosIcon
                    onClick={() => {
                      if (locPage !== locPages)
                        dispatch(
                          getLocations({
                            page: locPage + 1,
                          }),
                        );
                    }}
                    sx={locPage !== locPages ? styles.arrowAble : styles.arrowDisable}
                  />
                </Box>
              )}
            </>
          )}
        </Card>
      </Box>
    </Fade>
  );
};

export default ListInformation;
